import { CapacitorHttp } from '@capacitor/core';

import {
  endungVonMime,
  holeMedien as holeMedienAblage,
  MEDIEN_WURZEL,
  medienPfad,
  type MedienDeps,
  type MedienErgebnis,
} from '../medienAblage';
import { kennung, type MediumZumHolen } from './abbildung';
import { basicKopfzeile } from './client';

/**
 * Die Anhänge einer Classic-Prüfung holen — `GET /rest/media/{id}`.
 *
 * Die **Sätze** stehen nach dem Download in der Tabelle `Media` (`abbildung.ts`); hier kommen
 * die **Dateien** dazu. Gezeigt werden sie in der Vignette der Station
 * (Entscheidung des Nutzers, 2026-09-13).
 *
 * ⚠️ **Wo sie landen, steht in `server/medienAblage.ts`** — gemeinsam mit dem Relay-Gleis. Die
 * Ablage ist dieselbe Frage für beide (s. `memory/zweigleisig.md`); verschieden sind nur die
 * Adresse und die Kennung. Die Namen werden hier weitergereicht, damit an den Aufrufstellen
 * nichts umzuschreiben war.
 *
 * ⚠️ **Ein Fehlschlag bricht den Download nicht ab.** Der Classic-Client tut genau das („die
 * Prüfung ist damit wahrscheinlich nicht korrekt nutzbar"). Eine Prüfung ohne ein Anhangsbild
 * ist aber brauchbar; eine gar nicht geladene ist es nicht.
 */

export { MEDIEN_WURZEL, endungVonMime, medienPfad };
export type { MedienErgebnis };

/**
 * ⚠️ **Classic legt unter seiner eigenen Kennung ab** (`classic:media:12`), nicht unter der
 * nackten Zahl aus der Adresse: unter der Zahl allein könnten zwei Server dieselbe Datei
 * meinen, sobald ein Gerät nacheinander an beiden hängt.
 */
export async function holeMedien(
  medien: MediumZumHolen[],
  deps: Omit<MedienDeps, 'hole' | 'ablageId'> & { hole: (id: number) => Promise<string> },
): Promise<MedienErgebnis> {
  return holeMedienAblage(medien, {
    ...deps,
    hole: (id) => deps.hole(Number(id)),
    ablageId: (id) => kennung.media(id),
  });
}

/**
 * Der Weg hinaus.
 *
 * ⚠️ **Nicht über den `ServerClient`**: dessen Transport reicht Text durch (`responseType:
 * 'text'`), und ein JPEG als Text gelesen ist verloren. `CapacitorHttp` mit `responseType:
 * 'blob'` gibt die Bytes als Base64 zurück — auf dem Gerät wie im Browser, weil die
 * Web-Umsetzung des Plugins dasselbe tut.
 */
export function medienHoler(zugang: { baseUrl: string; login: string; password: string }) {
  return async (id: number): Promise<string> => {
    const antwort = await CapacitorHttp.request({
      url: zugang.baseUrl.replace(/\/+$/, '') + '/rest/media/' + encodeURIComponent(String(id)),
      method: 'GET',
      headers: { Authorization: basicKopfzeile(zugang.login, zugang.password) },
      responseType: 'blob',
      connectTimeout: 30000,
      readTimeout: 30000,
    });
    if (antwort.status < 200 || antwort.status >= 300) {
      throw new Error('Der Server antwortete ' + antwort.status + '.');
    }
    if (typeof antwort.data !== 'string' || antwort.data === '') {
      throw new Error('Der Server lieferte keine Daten.');
    }
    // Manche Wege legen einen `data:`-Vorspann davor; die Ablage will nur die Bytes.
    const komma = antwort.data.indexOf(',');
    return antwort.data.startsWith('data:') && komma > 0 ? antwort.data.slice(komma + 1) : antwort.data;
  };
}
